import { surfaceSupportRadius, surfaceSupportNormal } from '@hyperlight/shared';
/** Ambient W4 residents rendered directly from resident CityGraphs. */
import * as THREE from "three";
import {
  LEVEL_REGION, RegionLifeIndex, collapseRegion, dirToCell, dirToLocal,
  directionDayFrac, homeOwner, homeCapitalAssignment,
  edgeNeighbor, indexRegionRoster, interiorClassForUse, localToDir, packCell, parseRegionNpcId, seedInt,
  canonicalCastAtCapital, regionSettlements,
  regionNpcDef, settlementName, SIDE_NEG_U, SIDE_NEG_V, SIDE_POS_U, SIDE_POS_V,
  type AgentRecord, type CityGraph, type IndexedRegionRoster, type NpcDef,
  type NpcPose, type RegionChronicle,
  type CanonicalNpcPlacement,
} from "@hyperlight/shared";
import { Avatar } from "../character/avatar";
import { driveAvatar, type AvatarDriveClock, type AvatarLodPolicy } from "../character/avatarDriver";
import { buildingInteriorAnchors, type WorldAnchor } from "./settlement/buildingMeshes";
import type { SettlementRenderer } from "./settlement/renderer";
import type { Planet } from "./planet";
import type { StarSystem } from "./starsystem";
import { ambientResidentIndices } from "./regionNpcSampling";
import {
  NPC_CATCHUP_SPEED, advanceNpcCatchup, beginNpcCatchup, type NpcCatchupState,
} from "./regionNpcCatchup";

const VIEW_RANGE = 220;
const VIEW_DROP = 260;
const NEAR_RANGE = 300;
/** Perf quick-win (2026-07-31): the far half of a city is invisible from
 *  inside a shop — the sampling radius shrinks while the player is indoors. */
const NEAR_RANGE_INDOORS = 150;
const REGION_RANGE = 1200;
const AMBIENT_PER_REGION = 128; // halved with the 1.5-per-house density call (2026-07-31)
/** P4 staffing: interior-workplace residents force-included per settlement. */
const STAFF_PER_SETTLEMENT = 10;

// ── NPC ACTIVITY EMOTES (2026-07-31, UAL1/2) ─────────────────────────────────
// Stationary residents play a looping activity clip instead of the neutral
// idle: shopkeepers serve counters, farmers tend rows, taverns talk and drink.
// The pick rotates every EMOTE_BEAT_S seconds per NPC (deterministic in the
// id + beat), with plain-idle beats mixed in so streets don't read as a stage.
const EMOTE_BEAT_S = 16;
// LEAN-reading emotes (foldarms/tired) and the counter-service set fire ONLY
// when the resident RESOLVED to a counter anchor — see COUNTER_EMOTES below.
// Role alone is not enough: a keeper who ended up at a seat, or out on the
// street, was still playing the lean and resting on nothing (2026-07-31 user
// call, "NPCs lean where no counter exists"). The pools below stay per-role;
// activityEmote filters them against the live anchor.
// CONVERSATIONAL emotes (talk/nod) fire only in PAIRS (pairEmote) — a solo
// resident chatting at nobody reads broken (2026-07-31 screenshot). Solo
// rotations stay physical: drink, eat, dance, look around, work the trade.
const ACTIVITY_EMOTES: Record<string, readonly string[]> = {
  eat: ["emo_drink", "emo_consume"],
  social: ["emo_drink", "emo_dance", "emo_lookaround"],
  stroll: ["emo_lookaround"],
  work: ["emo_fixing", "emo_counter_idle", "emo_chop"],
  errand: ["emo_lookaround"], // browsing the wares
  worship: ["emo_lookaround"],
};
const WORK_EMOTES: Record<string, readonly string[]> = {
  farmer: ["emo_farm_harvest", "emo_farm_plant", "emo_farm_water", "emo_farm_scatter"],
  artisan: ["emo_fixing", "emo_chop"],
  trader: ["emo_counter_idle", "emo_counter_give", "emo_counter_show", "emo_foldarms"],
  keeper: ["emo_counter_idle", "emo_counter_show", "emo_foldarms"],
  elder: ["emo_lookaround"],
  warden: ["emo_lookaround"],
};
// ── BED SLEEP TRANSFORM (2026-07-31 play report) ─────────────────────────────
// "Sleeping NPCs must have eyes closed, be aligned with their beds (head on
// pillow), and not float above the mattress."
//
// ALIGNMENT. The bed anchor's `face` is the piece's local +z — which for a
// wall-hugging bed points OUT of the wall, i.e. straight ACROSS the mattress.
// Lying along it laid every sleeper crossways. The body runs along the bed's
// LONG axis instead, which is the piece's local ±x (`WorldAnchor.right`).
// PILLOW END: the int-bed kit doc authors its headboard and pillow at local
// −x (kit.ts: pillow box at x = −0.6, headboard at x = −0.99) — that is the
// rule, and it needs no wall/nightstand tie-break because the pillow is part
// of the mesh, not of the placement. So head = −right.
// FACING (2026-08-02 play report — "sleeping is upside down"): the IdleToLay
// clip lies the body down BACKWARD — the head lands BEHIND the avatar's
// facing, not in front of it. So the sleeper FACES THE FOOT END (+right) and
// the lie-back puts the head on the pillow. Pointing the facing at the pillow
// put every head at the foot of the bed.
// PILLOW CLEARANCE: the body is centre-anchored, so a centred sleeper's head
// reached past the pillow into the headboard — the body shifts
// BED_FOOTWARD_M toward the foot so the head RESTS on the pillow.
//
// HEIGHT. int-bed: frame 0 → 0.35, linen mattress box centred 0.43 with height
// 0.16 → MATTRESS TOP = 0.51 above the piece origin, and the bake plants the
// piece on exactly the plane the anchor reports. The group is seated 0.12 m
// LOWER than that because the avatar's ground conform (CONFORM_EMOTES in
// avatar.ts) drops the body until its lowest bone rests CONFORM_REST_Y = 0.12
// above the group origin — net: the sleeper's back/heels touch the mattress
// instead of hovering over it (the old figure was a hand-tuned +0.55/+0.58,
// which is the BLANKET top and left the whole body a body-radius too high).
/** int-bed mattress top above the piece origin (kit.ts int-bed doc). */
export const BED_MATTRESS_TOP_M = 0.51;
/** Avatar ground-conform seat allowance (avatar.ts CONFORM_REST_Y). */
const CONFORM_REST_Y_M = 0.12;
/** Group lift above the bed anchor's floor plane for a sleeper. */
export const SLEEP_LIFT_M = BED_MATTRESS_TOP_M - CONFORM_REST_Y_M;
/** Along-bed shift toward the FOOT end so the lie-back leaves the head ON the
 *  pillow instead of through the headboard. */
export const BED_FOOTWARD_M = 0.2;
/** Planet-local direction from the FOOT end to the PILLOW end of a bed anchor
 *  (where the HEAD ends up). Pure (unit-tested headlessly). */
export function bedHeadDir(anchor: Pick<WorldAnchor, "right">): [number, number, number] {
  return [-anchor.right[0], -anchor.right[1], -anchor.right[2]];
}
/** The heading (radians in the region's t1/t2 tangent frame) an anchored
 *  resident should hold. A sleeper FACES THE FOOT of the bed — the lie-down
 *  clip lays the body backward, so the head lands at the pillow (2026-08-02
 *  flip: facing the pillow put every head at the foot). Everyone else faces
 *  the way their anchor faces. Pure — the yaw math is unit-tested. */
export function anchorHeading(
  anchor: Pick<WorldAnchor, "kind" | "face" | "right">, sleeping: boolean,
  t1: readonly [number, number, number], t2: readonly [number, number, number],
): number {
  const hd = bedHeadDir(anchor);
  const d = sleeping && anchor.kind === "bed"
    ? [-hd[0], -hd[1], -hd[2]] // face the FOOT; lying back puts the head at −right (the pillow)
    : anchor.face;
  return Math.atan2(
    d[0] * t2[0] + d[1] * t2[1] + d[2] * t2[2],
    d[0] * t1[0] + d[1] * t1[1] + d[2] * t1[2]);
}

// ── P-B3: NPC-to-NPC conversations ───────────────────────────────────────────
// Stationary social/eat residents at the SAME venue pair off (sorted-id,
// deterministic) and stand facing each other, trading talk/nod emotes. The
// pair overrides the anchor spread — a conversation beats standing alone at
// separate seats.
const PAIR_GAP_M = 1.35;
interface PairPose { u: number; v: number; heading: number; partner: string }
function pairEmote(id: string, beat: number): string {
  const s = seedInt(id);
  const r = (s + beat) % 6;
  return r < 3 ? "emo_talk" : r < 5 ? "emo_yes" : "emo_drink";
}

// COUNTER-BOUND emotes (2026-07-31 play report: "NPCs lean where no counter
// exists"). Every clip here reads as a body resting on a waist-high surface —
// the counter-service set plus the folded-arms/tired leans. Gating lives in
// ONE place (activityEmote's filter) and keys off the RESOLVED anchor, not the
// role: a keeper standing at a seat or out on the street is not at a counter,
// and the role pool alone could never know that.
const COUNTER_EMOTES: ReadonlySet<string> = new Set([
  "emo_counter_idle", "emo_counter_give", "emo_counter_show",
  "emo_foldarms", "emo_tired",
]);
/** Upright stand-in when a role's whole pool turns out to be counter-bound
 *  (trader/keeper are counter clips end to end): a shopkeeper away from their
 *  counter glances about and tidies rather than leaning on thin air. */
const OFF_COUNTER_EMOTES: readonly string[] = ["emo_lookaround", "emo_fixing"];

/**
 * The emote a resident plays this beat — PURE f(role, activity, anchorKind,
 * beat), so the gating is testable headlessly (test:regionnpcs).
 * `anchorKind` is the interior anchor the resident actually resolved to
 * (null = none / outdoors); counter clips are only legal when it is "counter".
 * Determinism: the pick is a hash of (id seed, beat) — no sequential stream —
 * so filtering the pool cannot desync anything.
 */
export function activityEmote(
  npc: { id: string; role: string }, pose: NpcPose, beat: number,
  anchorKind: string | null = null,
): string {
  // Indoor sleepers hold the lie-down (P-B2 bed rendering) — no rotation.
  if (pose.activity === "sleep") return pose.indoors === true ? "emo_lay" : "";
  const pool = pose.activity === "work"
    ? WORK_EMOTES[npc.role] ?? ACTIVITY_EMOTES.work
    : ACTIVITY_EMOTES[pose.activity];
  if (!pool || pool.length === 0) return "";
  const atCounter = anchorKind === "counter";
  let list = atCounter ? pool : pool.filter((e) => !COUNTER_EMOTES.has(e));
  if (list.length === 0) list = OFF_COUNTER_EMOTES;
  const seed = seedInt(npc.id);
  // ~2 of every 5 beats rest on the plain idle.
  if ((((seed >>> 3) + beat) % 5) < 2) return "";
  return list[(seed + beat) % list.length];
}
// ~1.5 residents per house on the street (2026-07-31 user call — "TOO many
// NPCs"): parcels alternate 1 and 2 ambient residents via the region sampler.
const AMBIENT_PER_PARCEL = 2;
const SWEEP_PER_FRAME = 24; // 48 → 24 (2026-07-31 perf quick-win: halves steady-state poseAt volume)
const VIEW_MAX = 36;
const VIEW_KEEP = 44;
const SPAWN_PER_FRAME = 2;
// Citizens never substitute a visual proxy inside their 260 m view lifetime.
// The policy below controls animation cadence only; focus stays full-rate.
const CITIZEN_RENDER_LOD: AvatarLodPolicy = {
  midDistance: 120,
  farDistance: Infinity,
};
const CITIZEN_ANIMATION_LOD: AvatarLodPolicy = {
  ...CITIZEN_RENDER_LOD,
  midDistance: 80,
  midStride: 2,
  // Perf quick-win (2026-07-31): a third animation tier for background
  // citizens — quarter-rate gait past 160 m.
  distantDistance: 160,
  distantStride: 6,
};
/** Perf quick-win (2026-07-31): only the NEAREST citizens render into the
 *  shadow map — each shadow caster is a second full skinning pass, and at
 *  street scale a shadow 50 m away is invisible. */
const CITIZEN_SHADOW_RANGE_M = 45;
const FOCUSED_LOD: AvatarLodPolicy = { midDistance: Infinity, farDistance: Infinity };

export interface NpcLifeDebugOptions {
  /** Draw existing avatars. False preserves views and simulation for a GPU A/B. */
  render: boolean;
  /** Run the player-grade Avatar animation/state-machine update. */
  animate: boolean;
  /** Keep only actively blended clips registered with each citizen mixer. */
  sparseMixer: boolean;
  /** Use town-specific render LOD distances instead of remote-player distances. */
  citizenLod: boolean;
  /** Stagger citizen animation at distance instead of updating every visible
   *  resident on the same frame. */
  distanceAnimation: boolean;
  /** Run the per-avatar cloth solver. Only meaningful while animate is true. */
  cloth: boolean;
  /** Diagnostic legacy mode: run cloth on every citizen, including outfits
   *  that have no cloth-skinned geometry. */
  clothAll: boolean;
  /** Run terrain foot IK. Only meaningful while animate is true. */
  footIk: boolean;
  /** Diagnostic legacy mode: run foot IK on the whole crowd instead of only
   *  the focused/talking citizen. */
  footIkAll: boolean;
  /** Include citizen meshes in directional-light shadow maps. */
  shadows: boolean;
}

export interface NpcLifeProfileStats {
  frames: number;
  viewsMean: number;
  viewsMax: number;
  totalMeanMs: number;
  totalMaxMs: number;
  refreshMeanMs: number;
  refreshMaxMs: number;
  selectionMeanMs: number;
  spawnMeanMs: number;
  spawnMaxMs: number;
  poseMeanMs: number;
  animationMeanMs: number;
  dropMeanMs: number;
}

interface NpcLifeProfileAccum {
  frames: number;
  views: number;
  viewsMax: number;
  total: number;
  totalMax: number;
  refresh: number;
  refreshMax: number;
  selection: number;
  spawn: number;
  spawnMax: number;
  pose: number;
  animation: number;
  drop: number;
}

const DEFAULT_DEBUG: NpcLifeDebugOptions = {
  render: true,
  animate: true,
  sparseMixer: true,
  citizenLod: true,
  distanceAnimation: true,
  cloth: true,
  clothAll: false,
  footIk: true,
  footIkAll: false,
  shadows: true,
};

function emptyProfile(): NpcLifeProfileAccum {
  return {
    frames: 0, views: 0, viewsMax: 0,
    total: 0, totalMax: 0,
    refresh: 0, refreshMax: 0,
    selection: 0, spawn: 0, spawnMax: 0,
    pose: 0, animation: 0, drop: 0,
  };
}

interface ResidentSample {
  agent: AgentRecord | null;
  npc: NpcDef;
  pose: NpcPose;
  settlementName: string;
}

interface CanonicalSample {
  placement: CanonicalNpcPlacement;
  settlementName: string;
}

interface ActiveRegion {
  key: string;
  planet: Planet;
  planetIdx: number;
  graph: CityGraph;
  chronicle: RegionChronicle;
  roster: IndexedRegionRoster;
  life: RegionLifeIndex;
  offsets: number[];
  population: number;
  eligible: number;
  ambient: Uint32Array;
  ambientSet: Set<number>;
  near: Map<number, ResidentSample>;
  canonical: CanonicalSample[];
  sweep: number;
  /** DEBUG (hl-house-labels): lazy parcel → residents/workers name cache. */
  people?: Map<number, { residents: string[]; workers: string[] }>;
}

interface NpcView {
  npc: NpcDef;
  avatar: Avatar;
  yaw: number;
  regionKey: string;
  held: boolean;
  holdU: number;
  holdV: number;
  holdLocal?: THREE.Vector3;
  catchup: NpcCatchupState | null;
  lod: "near" | "mid" | "far";
  distance: number;
  animationPhase: number;
  animationClock: AvatarDriveClock;
  /** Is the `sleep` face preset currently held? (Edge-triggered so the sleep
   *  face is set/released once, never every frame.) */
  faceAsleep?: boolean;
  /** Last driven PLANET-LOCAL position + its planet (observe raycast source). */
  lastLocal?: THREE.Vector3;
  lastPlanetId?: string;
}

function animationPhase(id: string): number {
  let hash = 2166136261;
  for (let i = 0; i < id.length; i++) hash = Math.imul(hash ^ id.charCodeAt(i), 16777619);
  return hash >>> 0;
}

function residentSettlementName(agent: AgentRecord): string {
  return settlementName(agent.settlementId);
}

function neighbourhood(radius: number, cell: ReturnType<typeof dirToCell>): ReturnType<typeof dirToCell>[] {
  const negU = edgeNeighbor(radius, cell, SIDE_NEG_U), posU = edgeNeighbor(radius, cell, SIDE_POS_U);
  const negV = edgeNeighbor(radius, cell, SIDE_NEG_V), posV = edgeNeighbor(radius, cell, SIDE_POS_V);
  const all = [
    cell, negU, posU, negV, posV,
    edgeNeighbor(radius, negU, SIDE_NEG_V), edgeNeighbor(radius, negU, SIDE_POS_V),
    edgeNeighbor(radius, posU, SIDE_NEG_V), edgeNeighbor(radius, posU, SIDE_POS_V),
  ];
  const seen = new Set<number>();
  return all.filter((candidate) => {
    const key = packCell(candidate);
    if (seen.has(key)) return false;
    seen.add(key); return true;
  });
}

export class RegionNpcLife {
  private views = new Map<string, NpcView>();
  private active: ActiveRegion[] = [];
  private regionCache = new Map<string, ActiveRegion>();
  private evalTimer = 0;
  private lodFrame = 0;
  private focusId: string | null = null;
  private focusHold = false;
  private visible = true;
  private playerSys = new THREE.Vector3();
  private camSys = new THREE.Vector3();
  private local = new THREE.Vector3();
  private probeLocal = new THREE.Vector3();
  private probeNormal = [0, 0, 0];
  private probeInv = new THREE.Quaternion();
  private tmp = new THREE.Vector3();
  private tmp2 = new THREE.Vector3();
  private emoteClock = 0;
  private emoteBeat = 0;
  private pairTick = -1;
  private playerIndoors = false;
  /** main.ts wires the indoors signal each frame (sampling radius shrink). */
  setPlayerIndoors(indoors: boolean): void { this.playerIndoors = indoors; }
  /** P-B3: npc id → conversation stance (position + facing + partner). */
  private pairs = new Map<string, PairPose>();
  private up = new THREE.Vector3();
  private fwd = new THREE.Vector3();
  private m = new THREE.Matrix4();
  private t1 = new THREE.Vector3();
  private t2 = new THREE.Vector3();
  private dir = [0, 0, 0];
  private uv: [number, number] = [0, 0];
  private currentTimeSec = 0;
  private debug: NpcLifeDebugOptions = { ...DEFAULT_DEBUG };
  private profiling = false;
  private profile = emptyProfile();

  constructor(private scene: THREE.Scene) {}

  /** Dev/render control: analytic identities remain intact while avatars are hidden. */
  setVisible(visible: boolean): void {
    this.visible = visible;
    if (!visible) for (const id of [...this.views.keys()]) this.drop(id);
  }

  setFocus(npcId: string | null, hold: boolean, playerPos: THREE.Vector3): void {
    this.focusId = npcId;
    this.focusHold = hold;
    this.playerSys.copy(playerPos);
  }

  /** Live A/B controls. Existing views stay resident so changing one component
   *  does not mix spawn/despawn churn into the measurement. */
  setDebugOptions(options: Partial<NpcLifeDebugOptions>): NpcLifeDebugOptions {
    Object.assign(this.debug, options);
    for (const view of this.views.values()) this.applyDebug(view);
    return { ...this.debug };
  }

  debugOptions(): NpcLifeDebugOptions { return { ...this.debug }; }

  setProfiling(enabled: boolean): void {
    this.profiling = enabled;
    this.resetProfile();
  }

  resetProfile(): void { this.profile = emptyProfile(); }

  profileStats(): NpcLifeProfileStats {
    const p = this.profile;
    const n = Math.max(1, p.frames);
    return {
      frames: p.frames,
      viewsMean: p.views / n,
      viewsMax: p.viewsMax,
      totalMeanMs: p.total / n,
      totalMaxMs: p.totalMax,
      refreshMeanMs: p.refresh / n,
      refreshMaxMs: p.refreshMax,
      selectionMeanMs: p.selection / n,
      spawnMeanMs: p.spawn / n,
      spawnMaxMs: p.spawnMax,
      poseMeanMs: p.pose / n,
      animationMeanMs: p.animation / n,
      dropMeanMs: p.drop / n,
    };
  }

  private applyDebug(view: NpcView): void {
    view.avatar.group.visible = this.debug.render;
    view.avatar.setSparseMixerActionsEnabled(this.debug.sparseMixer);
    view.avatar.footIkEnabled = this.debug.footIk && this.debug.footIkAll;
    // Only the drifter back piece is skinned to the cl_back_drifter_* chains.
    // Cape/cloak cosmetics are rigid procedural meshes, so advancing their
    // cloth bones has no visible output.
    view.avatar.setClothSimulationEnabled(
      this.debug.cloth && (this.debug.clothAll || view.npc.style.torso === "drifter"),
    );
    view.avatar.setShadowsEnabled(this.debug.shadows);
  }

  private agentAt(region: ActiveRegion, globalIndex: number): AgentRecord {
    let settlement = region.offsets.length - 1;
    while (settlement > 0 && globalIndex < region.offsets[settlement]) settlement--;
    return region.roster.settlements[settlement].agentAt(globalIndex - region.offsets[settlement]);
  }

  private globalIndex(region: ActiveRegion, id: string): number {
    const parsed = parseRegionNpcId(id);
    if (!parsed) return -1;
    const settlement = region.roster.settlements.findIndex((entry) => entry.settlementId === parsed.settlementId);
    return settlement < 0 || parsed.agentIndex >= region.roster.settlements[settlement].population
      ? -1 : region.offsets[settlement] + parsed.agentIndex;
  }

  private refreshActive(system: StarSystem, camSys: THREE.Vector3): void {
    const owner = homeOwner(system.def.id);
    const capital = owner && system.planets[0] ? homeCapitalAssignment(system.planets[0].sampler) : null;
    const wanted = new Set<string>();
    const next: ActiveRegion[] = [];
    for (let pi = 0; pi < system.planets.length; pi++) {
      const planet = system.planets[pi], service = planet.regionService;
      if (!service) continue;
      this.local.copy(camSys).sub(planet.pose.center).applyQuaternion(planet.pose.invQuat);
      if (Math.abs(this.local.length() - planet.def.radius) > REGION_RANGE + 1500) continue;
      const centre = dirToCell(planet.def.radius, LEVEL_REGION, this.local.x, this.local.y, this.local.z);
      for (const cell of neighbourhood(planet.def.radius, centre)) {
        const rk = packCell(cell);
        if (!service.index.has(rk)) continue;
        const key = `${planet.def.seed}:${rk}`;
        wanted.add(key);
        let region = this.regionCache.get(key);
        if (!region) {
          const graph = service.region(cell);
          const chronicle = collapseRegion(planet.sampler, graph);
          const roster = indexRegionRoster(graph, chronicle, 512);
          if (roster.population === 0) continue;
          const offsets: number[] = [];
          let total = 0;
          for (const settlement of roster.settlements) { offsets.push(total); total += settlement.population; }
          const eligible: number[] = [];
          for (let settlementIndex = 0; settlementIndex < roster.settlements.length; settlementIndex++) {
            const offset = offsets[settlementIndex];
            for (const index of roster.settlements[settlementIndex].localAgentIndices(AMBIENT_PER_PARCEL)) {
              eligible.push(offset + index);
            }
          }
          const samplePositions = ambientResidentIndices(eligible.length, AMBIENT_PER_REGION, key);
          // P4 staffing force-include (2026-07-31): residents whose workplace
          // is a WALK-IN building are always ambient — shops, taverns and
          // halls read staffed even though ambient sampling is sparse.
          const staffed: number[] = [];
          for (let si = 0; si < roster.settlements.length; si++) {
            const s = roster.settlements[si];
            let taken = 0;
            for (let a = 0; a < s.population && taken < STAFF_PER_SETTLEMENT; a++) {
              const agent = s.agentAt(a);
              if (agent.workplaceParcel === null) continue;
              if (interiorClassForUse(graph.parcels[agent.workplaceParcel].use) === null) continue;
              staffed.push(offsets[si] + a); taken++;
            }
          }
          const ambientIdx = new Set<number>(staffed);
          for (const index of samplePositions) ambientIdx.add(eligible[index]);
          const ambient = Uint32Array.from(ambientIdx);
          const canonical = owner && pi === 0 && capital?.cellKey === rk
            ? (() => {
              const site = regionSettlements(planet.sampler, graph, chronicle)
                .find((settlement) => settlement.seedK === capital.k);
              // The FULL home id (multi-character: `home:<uid>.<cid>`) — the
              // canon-NPC ids embed it, and talk validation compares exactly.
              return site ? canonicalCastAtCapital(system.def.id, graph, chronicle, site)
                .map((placement) => ({ placement, settlementName: placement.settlement.name })) : [];
            })()
            : [];
          region = {
            key, planet, planetIdx: pi, graph, chronicle, roster,
            life: new RegionLifeIndex(graph, chronicle, 1024, 512), offsets,
            population: total, eligible: eligible.length, near: new Map(), sweep: 0,
            canonical,
            ambient, ambientSet: new Set(ambient),
          };
          this.regionCache.set(key, region);
        }
        next.push(region);
      }
    }
    this.active = next;
    for (const [key, region] of this.regionCache) if (!wanted.has(key)) {
      for (const sample of region.near.values()) this.drop(sample.npc.id);
      this.regionCache.delete(key);
    }
  }

  private camLocal(region: ActiveRegion, camSys: THREE.Vector3): { u: number; v: number; h: number } {
    this.local.copy(camSys).sub(region.planet.pose.center).applyQuaternion(region.planet.pose.invQuat);
    const radius = this.local.length() || 1;
    dirToLocal(region.graph.frame, this.local.x / radius, this.local.y / radius, this.local.z / radius, this.uv);
    const surface = surfaceSupportRadius(region.planet.sampler, this.local.x / radius, this.local.y / radius, this.local.z / radius);
    return { u: this.uv[0], v: this.uv[1], h: radius - surface };
  }

  /** Avatar foot-IK sampler in render space, scoped to this resident's
   * planet. Ambient avatars previously had no probe, leaving the walk clips'
   * raised toe pitch untouched even while standing still. */
  private probeGround(
    region: ActiveRegion,
    renderPos: THREE.Vector3,
    outPoint: THREE.Vector3,
    outNormal: THREE.Vector3,
  ): boolean {
    const planet = region.planet;
    // Use the interpolated RENDER quaternion in both directions. `pose.invQuat`
    // is the latest sim tick and would sawtooth against the smoothly rendered
    // resident/terrain at high refresh rates—the exact jitter IK must avoid.
    this.probeLocal.copy(renderPos).sub(planet.group.position)
      .applyQuaternion(this.probeInv.copy(planet.group.quaternion).invert());
    const length = this.probeLocal.length();
    if (length < 1e-3) return false;
    this.probeLocal.multiplyScalar(1 / length);
    let radius = surfaceSupportRadius(planet.sampler, this.probeLocal.x, this.probeLocal.y, this.probeLocal.z);
    // INDOORS (2026-08-02 "feet clip into the floor inside buildings"): the
    // terrain probe under a RAISED interior slab reads the ground below it, and
    // the avatar's foot conform dragged residents down through the floor. If
    // the avatar stands in an interior volume, the slab is the ground. Probe at
    // thigh height ABOVE the avatar's actual radius (the floor query is
    // level-banded — a terrain-height probe sits below the band).
    let onSlab = false;
    const sc = planet.settlementColliders as
      { interiorFloorRadiusAt?: (x: number, y: number, z: number) => number | null } | null | undefined;
    if (sc?.interiorFloorRadiusAt) {
      const probeR = length + 1.0;
      const ifr = sc.interiorFloorRadiusAt(
        this.probeLocal.x * probeR, this.probeLocal.y * probeR, this.probeLocal.z * probeR);
      if (typeof ifr === "number" && ifr + 0.05 > radius) { radius = ifr + 0.05; onSlab = true; }
    }
    outPoint.copy(this.probeLocal).multiplyScalar(radius)
      .applyQuaternion(planet.group.quaternion).add(planet.group.position);
    if (onSlab) {
      // A slab is flat: its normal is the pure radial (terrain normal would
      // tilt the feet to the buried ground's slope).
      outNormal.copy(this.probeLocal).applyQuaternion(planet.group.quaternion).normalize();
      return true;
    }
    surfaceSupportNormal(planet.sampler,
      this.probeLocal.x, this.probeLocal.y, this.probeLocal.z,
      this.probeNormal, 2,
    );
    outNormal.set(this.probeNormal[0], this.probeNormal[1], this.probeNormal[2])
      .applyQuaternion(planet.group.quaternion).normalize();
    return true;
  }

  private sample(region: ActiveRegion, index: number, tSec: number): ResidentSample {
    const existing = region.near.get(index);
    const agent = existing?.agent ?? this.agentAt(region, index);
    return {
      agent,
      npc: existing?.npc ?? regionNpcDef(region.graph, agent, region.chronicle),
      pose: region.life.poseAt(agent, this.currentSystem!.def, region.planetIdx, tSec),
      settlementName: existing?.settlementName ?? residentSettlementName(agent),
    };
  }

  private canonicalSample(region: ActiveRegion, sample: CanonicalSample, tSec: number): ResidentSample {
    const npc = sample.placement.npc;
    localToDir(region.graph.frame, npc.u, npc.v, this.dir);
    const dayFrac = directionDayFrac(this.currentSystem!.def, region.planetIdx, this.dir, tSec);
    const period = region.planet.def.spin.periodS;
    return {
      agent: null,
      npc,
      settlementName: sample.settlementName,
      pose: {
        u: npc.u, v: npc.v, activity: "work", moving: false, heading: 0,
        speed: 0, dayFrac, dayIndex: Math.floor(tSec / period - dayFrac + 0.5),
      },
    };
  }

  private currentSystem: StarSystem | null = null;

  update(dt: number, origin: THREE.Vector3, camPosRender: THREE.Vector3, system: StarSystem, tSec: number): void {
    this.emoteClock += dt;
    this.emoteBeat = Math.floor(this.emoteClock / EMOTE_BEAT_S);
    const profileOn = this.profiling;
    const updateStarted = profileOn ? performance.now() : 0;
    let refreshMs = 0, selectionMs = 0, spawnMs = 0, poseMs = 0, animationMs = 0, dropMs = 0;
    this.currentSystem = system;
    this.currentTimeSec = tSec;
    if (!this.visible) return;
    this.lodFrame++;
    const camSys = this.camSys.copy(camPosRender).add(origin);
    this.evalTimer -= dt;
    if (this.evalTimer <= 0) {
      this.evalTimer = 1;
      const started = profileOn ? performance.now() : 0;
      this.refreshActive(system, camSys);
      if (profileOn) refreshMs += performance.now() - started;
    }
    const selectionStarted = profileOn ? performance.now() : 0;
    const live = new Set<string>();
    const perRegion = Math.max(1, Math.floor(SWEEP_PER_FRAME / Math.max(1, this.active.length)));

    for (const region of this.active) {
      const camera = this.camLocal(region, camSys);
      const steps = Math.min(region.ambient.length, perRegion);
      for (let k = 0; k < steps; k++) {
        const sampleIndex = (region.sweep + k) % region.ambient.length;
        const index = region.ambient[sampleIndex];
        const existing = region.near.get(index);
        const agent = existing?.agent ?? this.agentAt(region, index);
        const pose = region.life.poseAt(agent, system.def, region.planetIdx, tSec);
        const d = Math.hypot(pose.u - camera.u, pose.v - camera.v, camera.h);
        // P-B2: INDOOR sleepers are rendered (lying in their beds) — only
        // outdoor/unhoused sleep stays culled (nothing to show).
        if ((pose.activity !== "sleep" || pose.moving || pose.indoors === true)
          && d < (this.playerIndoors ? NEAR_RANGE_INDOORS : NEAR_RANGE)) region.near.set(index, {
          agent, pose, npc: existing?.npc ?? regionNpcDef(region.graph, agent, region.chronicle),
          settlementName: existing?.settlementName ?? residentSettlementName(agent),
        });
        else region.near.delete(index);
      }
      region.sweep = region.ambient.length > 0 ? (region.sweep + steps) % region.ambient.length : 0;
      if (this.focusId) {
        const index = this.globalIndex(region, this.focusId);
        if (index >= 0 && !region.near.has(index)) region.near.set(index, this.sample(region, index, tSec));
      }
      for (const [index, sample] of region.near) if (!region.ambientSet.has(index) && sample.npc.id !== this.focusId) {
        region.near.delete(index);
      }
    }

    const candidates: { region: ActiveRegion; index: number | null; sample: ResidentSample; d: number }[] = [];
    // P-B3: pair stationary social/eat residents at the same venue into
    // face-to-face conversations (sorted-id — deterministic and stable).
    // Perf quick-win (2026-07-31): venue membership changes on schedule
    // timescales, not frame timescales — rebuild the pair map at 2 Hz.
    const pairTick = Math.floor(this.emoteClock * 2);
    const pairsStale = pairTick !== this.pairTick;
    this.pairTick = pairTick;
    if (pairsStale) {
    this.pairs.clear();
    for (const region of this.active) {
      const venues = new Map<string, { id: string; pose: NpcPose }[]>();
      for (const sample of region.near.values()) {
        const pose = sample.pose;
        if (pose.moving || (pose.activity !== "social" && pose.activity !== "eat")) continue;
        const venue = typeof pose.parcel === "number"
          ? `p${pose.parcel}` : `q${Math.round(pose.u / 9)}:${Math.round(pose.v / 9)}`;
        const list = venues.get(venue) ?? [];
        list.push({ id: sample.npc.id, pose });
        venues.set(venue, list);
      }
      for (const list of venues.values()) {
        if (list.length < 2) continue;
        list.sort((a, b) => a.id.localeCompare(b.id));
        for (let i = 0; i + 1 < list.length; i += 2) {
          const a = list[i], b = list[i + 1];
          const mu = (a.pose.u + b.pose.u) / 2, mv = (a.pose.v + b.pose.v) / 2;
          let du = b.pose.u - a.pose.u, dv = b.pose.v - a.pose.v;
          const dl = Math.hypot(du, dv);
          if (dl < 0.05) { // same anchor — split along a seeded direction
            const ang = (seedInt(a.id) % 628) / 100;
            du = Math.cos(ang); dv = Math.sin(ang);
          } else { du /= dl; dv /= dl; }
          const h = PAIR_GAP_M / 2;
          this.pairs.set(a.id, { u: mu - du * h, v: mv - dv * h, heading: Math.atan2(dv, du), partner: b.id });
          this.pairs.set(b.id, { u: mu + du * h, v: mv + dv * h, heading: Math.atan2(-dv, -du), partner: a.id });
        }
      }
    }
    } // pairsStale
    for (const region of this.active) {
      const camera = this.camLocal(region, camSys);
      for (const [index, sample] of region.near) {
        const d = Math.hypot(sample.pose.u - camera.u, sample.pose.v - camera.v, camera.h);
        if (d < VIEW_DROP) candidates.push({ region, index, sample, d });
      }
      for (const canonical of region.canonical) {
        const sample = this.canonicalSample(region, canonical, tSec);
        const d = Math.hypot(sample.pose.u - camera.u, sample.pose.v - camera.v, camera.h);
        if (d < VIEW_DROP) candidates.push({ region, index: null, sample, d });
      }
    }
    candidates.sort((a, b) => a.d - b.d || a.sample.npc.id.localeCompare(b.sample.npc.id));
    if (profileOn) selectionMs += performance.now() - selectionStarted;
    let spawned = 0;
    for (let rank = 0; rank < candidates.length; rank++) {
      const { region, index, sample: stale, d } = candidates[rank];
      const sample = rank < VIEW_KEEP && index !== null ? this.sample(region, index, tSec) : stale;
      const { npc, pose } = sample;
      let view = this.views.get(npc.id);
      if (view && rank >= VIEW_KEEP && d > 70) continue;
      let fresh = false;
      if (!view) {
        if (rank >= VIEW_MAX || d > VIEW_RANGE || spawned >= SPAWN_PER_FRAME) continue;
        spawned++; fresh = true;
        const spawnStarted = profileOn ? performance.now() : 0;
        // sharedMaterials: every citizen draws from ONE module-wide material set (all
        // per-citizen colour axes are COLOR_0 paint) — the town-perf deep fix.
        const avatar = new Avatar(this.scene, { sharedMaterials: true }); avatar.group.visible = true; avatar.setAmbient(true);
        avatar.setStyle(npc.style); avatar.setWeapon(null);
        avatar.groundProbe = (position, point, normal) => this.probeGround(region, position, point, normal);
        avatar.clothFrame = region.planet.group;
        view = {
          npc, avatar, yaw: pose.heading, regionKey: region.key,
          held: false, holdU: pose.u, holdV: pose.v, catchup: null,
          lod: "near", distance: d,
          animationPhase: animationPhase(npc.id), animationClock: { elapsed: 0 },
        };
        this.views.set(npc.id, view);
        this.applyDebug(view);
        if (profileOn) spawnMs += performance.now() - spawnStarted;
      }
      const poseStarted = profileOn ? performance.now() : 0;
      view.distance = d;
      live.add(npc.id);
      const focused = this.focusId === npc.id, held = focused && this.focusHold;
      // Residents are already rooted to the analytic terrain surface below. The
      // expensive two-foot solve is only perceptible at interaction distance, so
      // reserve it for the citizen the player is actively focusing. `footIkAll`
      // remains as a live-profiler control for comparing the legacy crowd path.
      view.avatar.footIkEnabled = this.debug.footIk && (this.debug.footIkAll || focused);
      // Perf quick-win (2026-07-31): only near citizens cast shadows — every
      // caster is a second full skinning pass into the shadow map.
      view.avatar.setShadowsEnabled(this.debug.shadows && d < CITIZEN_SHADOW_RANGE_M);
      let u = pose.u, v = pose.v, heading = pose.heading, moving = pose.moving, speed = pose.speed;
      if (held) {
        if (!view.held) {
          view.holdU = pose.u; view.holdV = pose.v; view.catchup = null;
          view.holdLocal = view.lastLocal?.clone();
          if (view.holdLocal) {
            const radius = view.holdLocal.length() || 1, uv: [number, number] = [0, 0];
            dirToLocal(region.graph.frame, view.holdLocal.x / radius, view.holdLocal.y / radius, view.holdLocal.z / radius, uv);
            [view.holdU, view.holdV] = uv;
          }
        }
        u = view.holdU; v = view.holdV; moving = false; speed = 0;
      } else {
        if (view.held && index !== null) view.catchup = beginNpcCatchup(region.life, view.holdU, view.holdV, pose.u, pose.v);
        if (view.catchup) {
          const step = advanceNpcCatchup(region.life, view.catchup, pose.u, pose.v, dt);
          view.catchup = step.state;
          u = step.u; v = step.v; heading = step.heading;
          moving = step.state !== null; speed = moving ? NPC_CATCHUP_SPEED : pose.speed;
          view.holdU = u; view.holdV = v;
        }
      }
      view.held = held;
      // P-B3: a conversation stance overrides everything else while
      // stationary — the pair stands face to face at their venue.
      const pair = !moving && !held && view.catchup === null ? this.pairs.get(npc.id) : undefined;
      if (pair) { u = pair.u; v = pair.v; heading = pair.heading; }
      // Keep the anchor's position, but a conversation now interrupts the work pose
      // and turns the resident toward the player, including behind a shop counter.
      const anchor = !pair && !moving && view.catchup === null && pose.indoors === true
        ? this.anchorFor(region, pose, npc.id) : null;
      const sleeping = pose.activity === "sleep";
      if (anchor) {
        this.t1.set(...region.graph.frame.t1);
        this.t2.set(...region.graph.frame.t2);
        // Sleepers lie ALONG their bed (head at the pillow end) — see the BED
        // SLEEP TRANSFORM header; everyone else faces their anchor's facing.
        heading = anchorHeading(anchor, sleeping, region.graph.frame.t1, region.graph.frame.t2);
      }
      const player = this.camLocal(region, this.playerSys);
      const targetYaw = held ? Math.atan2(player.v - v, player.u - u) : heading;
      let delta = (targetYaw - view.yaw) % (Math.PI * 2);
      if (delta > Math.PI) delta -= Math.PI * 2;
      if (delta < -Math.PI) delta += Math.PI * 2;
      view.yaw = fresh ? targetYaw : view.yaw + Math.max(-dt * 5.5, Math.min(dt * 5.5, delta));
      view.avatar.setTalking(held || pair !== undefined);
      view.avatar.setConversationPerformance(held);
      // EYES CLOSED WHILE ASLEEP (2026-07-31 play report). Edge-triggered: the
      // preset is held until the resident wakes. Applying it is free on a
      // face-less LOD — Avatar.applyFace early-outs on farLOD / no GLB body, so
      // the rig state simply never reaches a morph that isn't there.
      const wantAsleep = !held && sleeping && anchor !== null;
      if (wantAsleep !== (view.faceAsleep ?? false)) {
        view.avatar.faceRig.setEmote(wantAsleep ? "sleep" : null);
        view.faceAsleep = wantAsleep;
      }

      // P-B2: a stationary indoor resident snaps to the matching interior
      // anchor (counter/bed/seat); sleepers lie ON the bed (+0.55). Otherwise
      // ground at the terrain — clamped up to the interior floor when indoors
      // (raised slabs sit up to ~1.75 m above the terrain beneath them).
      localToDir(region.graph.frame, u, v, this.dir);
      if (anchor) {
        // +0.05: the VISUAL slab tops sit +0.02 above the collision floor
        // (z-fight inset) — feet must rest on the visual, not inside it.
        const lift = sleeping ? SLEEP_LIFT_M : 0.05;
        const al = Math.hypot(anchor.pos[0], anchor.pos[1], anchor.pos[2]) || 1;
        this.local.set(
          anchor.pos[0] * (1 + lift / al),
          anchor.pos[1] * (1 + lift / al),
          anchor.pos[2] * (1 + lift / al));
        // Sleepers shift toward the FOOT end so the head rests ON the pillow
        // (the centre-anchored lie-back reached into the headboard).
        if (sleeping && anchor.kind === "bed") {
          this.local.addScaledVector(
            this.tmp2.set(anchor.right[0], anchor.right[1], anchor.right[2]), BED_FOOTWARD_M);
        }
      } else {
        let surface = surfaceSupportRadius(region.planet.sampler, this.dir[0], this.dir[1], this.dir[2]);
        if (pose.indoors === true) {
          const sc = region.planet.settlementColliders as
            { interiorFloorRadiusAt?: (x: number, y: number, z: number) => number | null } | null | undefined;
          // Probe at MID-ROOM height: the floor query is level-banded, and a
          // terrain-height probe under a raised slab sits BELOW the band →
          // null → shin-deep residents (2026-07-31 screenshot).
          const probeR = surface + 1.5;
          const ifr = sc?.interiorFloorRadiusAt?.(
            this.dir[0] * probeR, this.dir[1] * probeR, this.dir[2] * probeR);
          if (typeof ifr === "number" && ifr > surface) surface = ifr + 0.05; // onto the VISUAL slab
        }
        this.local.set(this.dir[0] * surface, this.dir[1] * surface, this.dir[2] * surface);
      }
      if (held && view.holdLocal) this.local.copy(view.holdLocal);
      view.avatar.group.position.copy(this.local).applyQuaternion(region.planet.group.quaternion).add(region.planet.group.position);
      // Observe raycast source: keep the driven planet-local pose per view.
      (view.lastLocal ??= new THREE.Vector3()).copy(this.local);
      view.lastPlanetId = region.planet.def.id;
      this.up.set(this.dir[0], this.dir[1], this.dir[2]).applyQuaternion(region.planet.group.quaternion);
      view.avatar.setHeadLookTarget(focused ? this.tmp.copy(this.playerSys).sub(origin) : null);
      this.fwd.set(0, 0, 0)
        .addScaledVector(this.t1.set(...region.graph.frame.t1), Math.cos(view.yaw))
        .addScaledVector(this.t2.set(...region.graph.frame.t2), Math.sin(view.yaw))
        .applyQuaternion(region.planet.group.quaternion);
      this.fwd.addScaledVector(this.up, -this.fwd.dot(this.up));
      if (this.fwd.lengthSq() > 1e-6) {
        this.fwd.normalize(); this.m.lookAt(new THREE.Vector3(), this.fwd, this.up);
        view.avatar.group.quaternion.setFromRotationMatrix(this.m);
      }
      if (profileOn) poseMs += performance.now() - poseStarted;
      if (this.debug.animate) {
        const animationStarted = profileOn ? performance.now() : 0;
        const lod = focused
          ? FOCUSED_LOD
          : this.debug.citizenLod
            ? this.debug.distanceAnimation ? CITIZEN_ANIMATION_LOD : CITIZEN_RENDER_LOD
            : undefined;
        view.lod = driveAvatar(view.avatar, {
          weaponId: null, jet: "none", aiming: false, airborne: false,
          // NEVER crouch while asleep: the crouch branch of the locomotion
          // blend outranks the activity-emote branch, so a crouching sleeper
          // would squat on the mattress instead of playing the lie-down.
          crouch: !moving && !held && !sleeping && view.catchup === null && (pose.crouch ?? false), blocking: false,
          moveFwd: moving ? 1 : 0, moveRight: 0, climb: 0, pitch: 0, groundSpeed: speed,
          emote: !moving && view.catchup === null && !held
            ? (pair ? pairEmote(npc.id, this.emoteBeat)
              : activityEmote(view.npc, pose, this.emoteBeat, anchor?.kind ?? null)) : "",
        }, dt, camPosRender, this.lodFrame, lod, view.animationPhase, view.animationClock);
        if (profileOn) animationMs += performance.now() - animationStarted;
      }
    }
    const dropStarted = profileOn ? performance.now() : 0;
    for (const id of [...this.views.keys()]) if (!live.has(id)) this.drop(id);
    if (profileOn) {
      dropMs += performance.now() - dropStarted;
      const totalMs = performance.now() - updateStarted;
      const p = this.profile;
      p.frames++;
      p.views += this.views.size;
      p.viewsMax = Math.max(p.viewsMax, this.views.size);
      p.total += totalMs;
      p.totalMax = Math.max(p.totalMax, totalMs);
      p.refresh += refreshMs;
      p.refreshMax = Math.max(p.refreshMax, refreshMs);
      p.selection += selectionMs;
      p.spawn += spawnMs;
      p.spawnMax = Math.max(p.spawnMax, spawnMs);
      p.pose += poseMs;
      p.animation += animationMs;
      p.drop += dropMs;
    }
  }

  // ── P-B2: anchor-true indoor stations ───────────────────────────────────────
  // A stationary indoor resident stands at their building's MATCHING interior
  // anchor: shopkeepers behind counters, sleepers in beds, diners at seats.
  // Anchors resolve from the pure building plan (buildingInteriorAnchors) via
  // the renderer's retained records; cached per (region, parcel).
  private anchorCache = new Map<string, WorldAnchor[] | null>();
  private renderer: SettlementRenderer | null = null;
  /** Wire the settlement renderer (main.ts) — the anchor resolver reads its
   *  retained building records. */
  setRenderer(r: SettlementRenderer | null): void { this.renderer = r; }

  private static readonly ANCHOR_KINDS: Record<string, readonly string[]> = {
    sleep: ["bed"],
    work: ["counter", "hearth", "seat"],
    eat: ["seat", "hearth", "counter"],
    social: ["seat", "hearth"],
    // 2026-07-31 richer schedules: worshippers take the sanctum benches;
    // ERRAND deliberately has no entry — customers browse at the generic
    // indoor point instead of stealing the keeper's counter.
    worship: ["seat", "hearth"],
  };

  private anchorFor(region: ActiveRegion, pose: NpcPose, npcId: string): WorldAnchor | null {
    if (typeof pose.parcel !== "number" || !this.renderer) return null;
    const key = `${region.key}:${pose.parcel}`;
    let list = this.anchorCache.get(key);
    if (list === undefined) {
      list = null;
      for (const dv of this.renderer.debugBuildingsFor(region.planet)) {
        if (packCell(dv.cell) !== packCell(region.graph.regionId)) continue;
        list = buildingInteriorAnchors(dv.buildings, pose.parcel);
        break;
      }
      this.anchorCache.set(key, list);
      if (this.anchorCache.size > 512) {
        const first = this.anchorCache.keys().next().value;
        if (first !== undefined) this.anchorCache.delete(first);
      }
    }
    if (!list || list.length === 0) return null;
    const wanted = RegionNpcLife.ANCHOR_KINDS[pose.activity];
    if (!wanted) return null;
    for (const kind of wanted) {
      const matches = list.filter((a) => a.kind === kind);
      if (matches.length > 0) return matches[seedInt(npcId) % matches.length];
    }
    return null;
  }

  /** Who lives/works at a parcel — names from the region roster, built lazily
   *  per region and cached on it. Feeds the OBSERVE building window (2026-08-02;
   *  formerly the hl-house-labels debug sprites). */
  parcelPeople(planetSeed: string, rk: number, parcel: number): { residents: string[]; workers: string[] } | null {
    const region = this.regionCache.get(`${planetSeed}:${rk}`);
    if (!region) return null;
    let map = region.people;
    if (!map) {
      map = new Map();
      for (const s of region.roster.settlements) {
        for (let a = 0; a < s.population; a++) {
          const agent = s.agentAt(a);
          if (agent.homeParcel !== null) {
            let entry = map.get(agent.homeParcel);
            if (!entry) { entry = { residents: [], workers: [] }; map.set(agent.homeParcel, entry); }
            entry.residents.push(agent.name);
          }
          if (agent.workplaceParcel !== null && agent.workplaceParcel !== agent.homeParcel) {
            let entry = map.get(agent.workplaceParcel);
            if (!entry) { entry = { residents: [], workers: [] }; map.set(agent.workplaceParcel, entry); }
            entry.workers.push(agent.name);
          }
        }
      }
      region.people = map;
    }
    return map.get(parcel) ?? { residents: [], workers: [] };
  }

  /** OBSERVE provider (2026-08-02): the rendered citizen the reticle ray passes
   *  through — name + role for the observe window. Ray is planet-local; each
   *  live view keeps its last driven planet-local pose (lastLocal). */
  observeHit(planetId: string, origin: THREE.Vector3, dir: THREE.Vector3, maxDist: number):
    { dist: number; title: string; subtitle: string } | null {
    let bestT = Infinity;
    let best: NpcView | null = null;
    for (const view of this.views.values()) {
      const l = view.lastLocal;
      if (!l || view.lastPlanetId !== planetId) continue;
      // Two samples up the radial (belly + head) so chest AND head aims both
      // read — a single mid sphere made headshots miss the reading.
      const ul = 1 / Math.max(1e-9, l.length());
      for (const up of [0.65, 1.45]) {
        const sx = l.x * (1 + ul * up), sy = l.y * (1 + ul * up), sz = l.z * (1 + ul * up);
        const ox = sx - origin.x, oy = sy - origin.y, oz = sz - origin.z;
        const t = ox * dir.x + oy * dir.y + oz * dir.z;
        if (t < 0.5 || t > maxDist || t >= bestT) continue;
        const px = ox - dir.x * t, py = oy - dir.y * t, pz = oz - dir.z * t;
        // Forgiveness grows with distance (people get small on screen fast).
        const r = 0.8 + t * 0.012;
        if (px * px + py * py + pz * pz > r * r) continue;
        bestT = t;
        best = view;
      }
    }
    if (best === null) return null;
    const role = best.npc.role;
    return {
      dist: bestT,
      title: best.npc.name,
      subtitle: role ? role.charAt(0).toUpperCase() + role.slice(1) : "Wanderer",
    };
  }

  private poseSystem(region: ActiveRegion, pose: NpcPose, out: THREE.Vector3): THREE.Vector3 {
    localToDir(region.graph.frame, pose.u, pose.v, this.dir);
    let surface = surfaceSupportRadius(region.planet.sampler, this.dir[0], this.dir[1], this.dir[2]);
    // P4 indoor life: an indoor station stands on the INTERIOR floor slab, not
    // the terrain beneath it (raised slabs sit up to ~1.75 m above ground).
    // Probe at mid-room height — the query is level-banded (see the view path).
    if (pose.indoors === true) {
      const sc = region.planet.settlementColliders as
        { interiorFloorRadiusAt?: (x: number, y: number, z: number) => number | null } | null | undefined;
      const probeR = surface + 1.5;
      const ifr = sc?.interiorFloorRadiusAt?.(
        this.dir[0] * probeR, this.dir[1] * probeR, this.dir[2] * probeR);
      if (typeof ifr === "number" && ifr > surface) surface = ifr;
    }
    return out.set(this.dir[0], this.dir[1], this.dir[2]).multiplyScalar(surface)
      .applyQuaternion(region.planet.pose.quat).add(region.planet.pose.center);
  }

  nearest(posSys: THREE.Vector3, maxDist: number): { npc: NpcDef; settlementName: string; planetIdx: number; dayIndex: number; dist: number } | null {
    type Hit = { npc: NpcDef; settlementName: string; planetIdx: number; dayIndex: number; dist: number };
    let best: Hit | null = null;
    let preferred: Hit | null = null;
    for (const region of this.active) for (const sample of region.near.values()) {
      if (sample.pose.activity === "sleep" || !this.views.has(sample.npc.id)) continue;
      // P4 indoor life: no talking THROUGH walls — an indoor resident is only
      // targetable when the player stands in the SAME interior volume.
      if (sample.pose.indoors === true) {
        const sc = region.planet.settlementColliders as
          { indoorsAt?: (x: number, y: number, z: number) => number } | null | undefined;
        if (!sc?.indoorsAt || !region.planet.pose.invQuat) continue;
        const npcSys = this.poseSystem(region, sample.pose, this.tmp);
        this.tmp2.copy(npcSys).sub(region.planet.pose.center).applyQuaternion(region.planet.pose.invQuat);
        const nUp = this.tmp2.length() || 1;
        const npcVol = sc.indoorsAt(
          this.tmp2.x * (1 + 1 / nUp), this.tmp2.y * (1 + 1 / nUp), this.tmp2.z * (1 + 1 / nUp));
        this.tmp2.copy(posSys).sub(region.planet.pose.center).applyQuaternion(region.planet.pose.invQuat);
        const pUp = this.tmp2.length() || 1;
        const playerVol = sc.indoorsAt(
          this.tmp2.x * (1 + 1 / pUp), this.tmp2.y * (1 + 1 / pUp), this.tmp2.z * (1 + 1 / pUp));
        if (npcVol < 0 || npcVol !== playerVol) continue;
      }
      const d = this.poseSystem(region, sample.pose, this.tmp).distanceTo(posSys);
      const hit: Hit = {
        npc: sample.npc, settlementName: sample.settlementName,
        planetIdx: region.planetIdx, dayIndex: sample.pose.dayIndex, dist: d,
      };
      if (sample.npc.id === this.focusId && d < maxDist + 0.75) preferred = hit;
      if (d < maxDist && (!best || d < best.dist)) best = hit;
    }
    for (const region of this.active) for (const canonical of region.canonical) {
      const sample = this.canonicalSample(region, canonical, this.currentTimeSec);
      if (!this.views.has(sample.npc.id)) continue;
      const d = this.poseSystem(region, sample.pose, this.tmp).distanceTo(posSys);
      const hit: Hit = {
        npc: sample.npc, settlementName: sample.settlementName,
        planetIdx: region.planetIdx, dayIndex: sample.pose.dayIndex, dist: d,
      };
      if (sample.npc.id === this.focusId && d < maxDist + 0.75) preferred = hit;
      if (d < maxDist && (!best || d < best.dist)) best = hit;
    }
    // Keep the current glance target until another resident is meaningfully
    // closer. Without this small hysteresis, two neighbors at nearly equal
    // range alternated focus every tick and made both heads snap back/forth.
    return preferred && (!best || preferred.dist <= best.dist + 0.75) ? preferred : best;
  }

  poseWorldById(npcId: string, tSec: number): { pos: [number, number, number]; planetId: string; u: number; v: number; moving: boolean } | null {
    for (const region of this.active) {
      const canonical = region.canonical.find((sample) => sample.placement.npc.id === npcId);
      if (canonical) {
        const pose = this.canonicalSample(region, canonical, tSec).pose;
        const world = this.poseSystem(region, pose, this.tmp);
        return { pos: [world.x, world.y, world.z], planetId: region.planet.def.id, u: pose.u, v: pose.v, moving: false };
      }
      const index = this.globalIndex(region, npcId);
      if (index < 0) continue;
      const agent = this.agentAt(region, index), pose = region.life.poseAt(agent, this.currentSystem!.def, region.planetIdx, tSec);
      const world = this.poseSystem(region, pose, this.tmp);
      return { pos: [world.x, world.y, world.z], planetId: region.planet.def.id, u: pose.u, v: pose.v, moving: pose.moving };
    }
    return null;
  }

  /** Lightweight console probe for validating census sampling and view budgets. */
  stats(): { visible: boolean; activeRegions: number; census: number; eligible: number; sampled: number; nearby: number; views: number; lodMid: number; lodFar: number; distanceP50: number; distanceP90: number; distanceMax: number } {
    const distances = [...this.views.values()].map((view) => view.distance).sort((a, b) => a - b);
    const percentile = (p: number): number => distances.length
      ? distances[Math.min(distances.length - 1, Math.floor((distances.length - 1) * p))]
      : 0;
    return {
      visible: this.visible,
      activeRegions: this.active.length,
      census: this.active.reduce((sum, region) => sum + region.population, 0),
      eligible: this.active.reduce((sum, region) => sum + region.eligible, 0),
      sampled: this.active.reduce((sum, region) => sum + region.ambient.length, 0),
      nearby: this.active.reduce((sum, region) => sum + region.near.size, 0),
      views: this.views.size,
      lodMid: [...this.views.values()].filter((view) => view.lod === "mid").length,
      lodFar: [...this.views.values()].filter((view) => view.lod === "far").length,
      distanceP50: percentile(0.5),
      distanceP90: percentile(0.9),
      distanceMax: percentile(1),
    };
  }

  /** The live render avatar for a persona (dialog drives its FaceRig while talking). */
  avatarOf(npcId: string): Avatar | null {
    return this.views.get(npcId)?.avatar ?? null;
  }

  // live-portrait cache (allocated on first dialog, reused every frame)
  private portraitRT: THREE.WebGLRenderTarget | null = null;
  private portraitPixels: Uint8Array | null = null;
  private portraitImg: ImageData | null = null;
  private portraitCam: THREE.PerspectiveCamera | null = null;
  private portraitKey: THREE.PointLight | null = null;
  private portraitFill: THREE.AmbientLight | null = null;
  private portraitRestore: { object: THREE.Object3D; mask: number }[] = [];
  // scene lights carry the portrait illumination (sun/hemi — key+fill alone read black on
  // the stylized materials); a full scene traverse is too hot per frame, so cache the list
  private portraitSceneLights: THREE.Object3D[] = [];
  private portraitLightScanT = -Infinity;

  /** LIVE dialog portrait: render the persona's talking head into `context`'s 192² canvas.
   *  Called per frame (throttled by main) while the dialog is open — cached RT/buffers,
   *  same layer-31 isolation as the old one-shot snapshot, but lit by its OWN key+fill only
   *  (no full-scene light traverse per frame). Returns false when the npc view is gone. */
  livePortraitInto(npcId: string, renderer: THREE.WebGLRenderer, context: CanvasRenderingContext2D): boolean {
    const view = this.views.get(npcId);
    if (!view) return false;
    const size = 192, layer = 31;
    const target = (this.portraitRT ??= (() => {
      const rt = new THREE.WebGLRenderTarget(size, size);
      // `renderer.outputColorSpace` applies to the DEFAULT framebuffer only; a
      // render target takes its own texture's space. Left linear, the readback
      // below is linear light that putImageData then treats as sRGB, and every
      // talking head comes out dark and over-saturated (measured on the roster
      // portraits, which share this technique: tan skin landing near 0x641805).
      rt.texture.colorSpace = THREE.SRGBColorSpace;
      return rt;
    })());
    const pixels = (this.portraitPixels ??= new Uint8Array(size * size * 4));
    const image = (this.portraitImg ??= context.createImageData(size, size));
    const camera = (this.portraitCam ??= new THREE.PerspectiveCamera(26, 1, 0.05, 4));
    const group = view.avatar.group;
    const previous = this.portraitRestore;
    previous.length = 0;
    group.traverse((object) => { previous.push({ object, mask: object.layers.mask }); object.layers.set(layer); });
    if (performance.now() - this.portraitLightScanT > 2000) {
      this.portraitLightScanT = performance.now();
      this.portraitSceneLights.length = 0;
      this.scene.traverse((object) => { if ((object as THREE.Light).isLight) this.portraitSceneLights.push(object); });
    }
    for (const light of this.portraitSceneLights) {
      previous.push({ object: light, mask: light.layers.mask });
      light.layers.enable(layer);
    }
    const up = this.up.set(0, 1, 0).applyQuaternion(group.quaternion);
    const forward = this.fwd.set(0, 0, -1).applyQuaternion(group.quaternion);
    const head = this.tmp.copy(group.position).addScaledVector(up, 1.58);
    camera.position.copy(head).addScaledVector(forward, 0.95).addScaledVector(up, 0.02);
    camera.up.copy(up); camera.lookAt(head); camera.layers.set(layer);
    const key = (this.portraitKey ??= (() => { const l = new THREE.PointLight(0xfff2dd, 30, 8); l.layers.set(layer); return l; })());
    const fill = (this.portraitFill ??= (() => { const l = new THREE.AmbientLight(0x99aacc, 1.1); l.layers.set(layer); return l; })());
    key.position.copy(camera.position).addScaledVector(up, 0.4);
    this.scene.add(key, fill);
    const background = this.scene.background; this.scene.background = null;
    const oldTarget = renderer.getRenderTarget();
    const oldColor = new THREE.Color(); renderer.getClearColor(oldColor); const oldAlpha = renderer.getClearAlpha();
    renderer.setRenderTarget(target); renderer.setClearColor(0x131e2e, 1); renderer.clear(); renderer.render(this.scene, camera);
    renderer.readRenderTargetPixels(target, 0, 0, size, size, pixels);
    renderer.setRenderTarget(oldTarget); renderer.setClearColor(oldColor, oldAlpha);
    this.scene.background = background; this.scene.remove(key, fill);
    for (const item of previous) item.object.layers.mask = item.mask;
    previous.length = 0;
    for (let y = 0; y < size; y++) image.data.set(pixels.subarray((size - 1 - y) * size * 4, (size - y) * size * 4), y * size * 4);
    context.putImageData(image, 0, 0);
    return true;
  }

  private drop(id: string): void {
    const view = this.views.get(id); if (!view) return;
    view.avatar.dispose(this.scene); this.views.delete(id);
  }

  dispose(): void {
    for (const id of [...this.views.keys()]) this.drop(id);
    this.active.length = 0; this.regionCache.clear(); this.currentSystem = null;
  }
}
